import React from 'react'
import { TextProps, View, ViewStyle } from 'react-native'
import { SvgProps } from 'react-native-svg'

import ErrorIcon from '@common/assets/svg/ErrorIcon'
import InfoIcon from '@common/assets/svg/InfoIcon'
import SuccessIcon from '@common/assets/svg/SuccessIcon'
import WarningIcon from '@common/assets/svg/WarningIcon'
import Button, { Props as ButtonProps } from '@common/components/Button'
import { isWeb } from '@common/config/env'
import useTheme from '@common/hooks/useTheme'
import spacings, { SPACING, SPACING_LG } from '@common/styles/spacings'
import common from '@common/styles/utils/common'
import flexbox from '@common/styles/utils/flexbox'

import Text, { TextWeight } from '../Text'

interface Props {
  title?: string | React.ReactNode
  titleWeight?: TextWeight
  text?: string | React.ReactNode
  type?: 'error' | 'warning' | 'success' | 'info' | 'info2'
  style?: ViewStyle
  children?: React.ReactNode
  size?: 'sm' | 'md'
  isTypeLabelHidden?: boolean
  buttonProps?: ButtonProps
  customIcon?: React.FC<SvgProps>
  withIcon?: boolean
  testID?: string
}

const ICON_MAP = {
  error: ErrorIcon,
  warning: WarningIcon,
  success: SuccessIcon,
  info: InfoIcon,
  info2: InfoIcon
}

const SIZES = {
  title: 20,
  text: 16,
  icon: 40
}

const SIZE_MULTIPLIERS = {
  sm: 0.8,
  md: 1
}

interface AlertVerticalTextProps extends TextProps {
  children: React.ReactNode
  size?: Props['size']
  type?: Props['type']
}

const AlertVerticalText: React.FC<AlertVerticalTextProps> = ({
  children,
  size = 'md',
  type = 'info',
  style,
  ...rest
}) => {
  const fontSize = SIZES.text * SIZE_MULTIPLIERS[size]

  return (
    <Text
      selectable
      fontSize={fontSize}
      weight="regular"
      // flexShrink: 1 — the parent is a row, where RN children don't shrink by
      // default; without it a long message overflows the alert instead of wrapping.
      // On web the message may be a single unbroken token (an ABI-encoded revert
      // reason: "Error code: 0x729a0b5b0000…"), which CSS refuses to break at the
      // default `word-break: normal` and lets spill out of the alert. Native RN
      // already breaks mid-word, so this is web-only.
      style={[
        spacings.mrTy,
        { textAlign: 'center', flexShrink: 1 },
        // `overflow-wrap: anywhere` (not `break-word`) is the one that also shrinks
        // the text's min-content width, so the alert box itself stops being stretched
        // wider than its parent by the unbreakable token.
        isWeb ? ({ overflowWrap: 'anywhere', minWidth: 0 } as any) : null,
        style
      ]}
      appearance={`${type}Text`}
      {...rest}
    >
      {children}
    </Text>
  )
}

const AlertVertical = ({
  title,
  titleWeight,
  text,
  type = 'info',
  style,
  children,
  size = 'md',
  isTypeLabelHidden = true,
  buttonProps,
  customIcon: CustomIcon,
  withIcon = true,
  testID
}: Props) => {
  const Icon = ICON_MAP[type]
  const { theme } = useTheme()
  const sizeMultiplier = SIZE_MULTIPLIERS[size]
  const titleFontSize = SIZES.title * sizeMultiplier

  return (
    <View
      style={[
        common.borderRadiusPrimary,
        flexbox.alignCenter,
        {
          // The alert is often placed inside a flex ROW, where RN children
          // neither shrink nor inherit a width. Without these two the box is
          // sized by its longest line of text, so a long message stretches the
          // alert past its container instead of wrapping — in the extension's
          // sign popup that means the error band spills out of the window.
          maxWidth: '100%',
          flexShrink: 1,
          paddingHorizontal: SPACING_LG * sizeMultiplier,
          paddingVertical: SPACING_LG * sizeMultiplier,
          borderWidth: 1,
          backgroundColor: theme[`${type}Background`],
          borderColor: theme[`${type}Decorative`]
        },
        style
      ]}
      testID={testID}
    >
      {!!withIcon && (
        <View
          style={{
            marginBottom: SPACING * sizeMultiplier
          }}
        >
          {CustomIcon ? (
            <CustomIcon />
          ) : (
            <Icon
              width={SIZES.icon * sizeMultiplier}
              height={SIZES.icon * sizeMultiplier}
              color={theme[`${type}Decorative`]}
            />
          )}
        </View>
      )}
      {!!title && (
        <Text
          style={[
            text ? spacings.mbTy : {},
            {
              textAlign: 'center'
            }
          ]}
        >
          {!isTypeLabelHidden && (
            <Text
              selectable
              appearance={`${type}Text`}
              fontSize={titleFontSize}
              weight={titleWeight || 'semiBold'}
              style={{ textTransform: 'capitalize' }}
            >
              {type}:{' '}
            </Text>
          )}
          <Text
            selectable
            appearance={`${type}Text`}
            fontSize={titleFontSize}
            weight={titleWeight || 'semiBold'}
          >
            {title}
          </Text>
        </Text>
      )}
      <View
        style={[
          flexbox.directionRow,
          flexbox.alignCenter,
          flexbox.justifyCenter,
          {
            maxWidth: '100%'
          }
        ]}
      >
        {!!text &&
          (typeof text === 'string' ? (
            <AlertVerticalText size={size} type={type}>
              {text}{' '}
            </AlertVerticalText>
          ) : (
            text
          ))}
      </View>
      {buttonProps && (
        <Button
          style={{
            alignSelf: 'flex-end',
            ...spacings.mtTy
          }}
          textStyle={type === 'error' && { fontSize: 14 }}
          size="small"
          type="primary"
          hasBottomSpacing={false}
          text={buttonProps.text}
          onPress={buttonProps.onPress}
          {...buttonProps}
        />
      )}
      {children}
    </View>
  )
}

AlertVertical.Text = AlertVerticalText

export default AlertVertical
